import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    Card,
    CardBody,
    Badge,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Input,
    Button,
    UncontrolledTooltip,
    FormGroup,
    Label,
} from "reactstrap";
import { MoreHorizontal, Edit, Trash2, Grid } from "react-feather";
import { fetchProductsByFilter } from "../../../../../api-handle/productApi";
import ProductCard from "./ProductCard";
import { useDebounce } from "../../../hooks/debounce";

export const Loader = () => {
    return (
        <div
            style={{
                width: "100%",
                minHeight: "200px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <div
                style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #e0e0e0",
                    borderTop: "4px solid #0d6efd",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                }}
            />
            <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
            </style>
        </div>
    );
};



const ProductTable = ({ onEdit, onDelete, onDeactivate, refresh, filteredBrand = "", filteredCategory = "", showTable, searchQuery, filteredSubCategory = "" }) => {
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("name");
    const [sortOrder, setSortOrder] = useState("asc");
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState({
        description: true,
        descriptionAr: true,
        category: true,
        stock: true,
        status: true,
    });

    const toggleDropdown = (id) => {
        setDropdownOpen(dropdownOpen === id ? null : id);
    };
    const debounceSearch = useDebounce(searchQuery, 300)
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await fetchProductsByFilter({
                page,
                size: rowsPerPage,
                sortColumn,
                sortOrder,
                search: debounceSearch,
                brand: filteredBrand,
                category: filteredCategory,
                subCategory: filteredSubCategory
            });

            setData(response.filteredData.products);
            setTotalRows(response.filteredData.pagination.totalItems);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [page, rowsPerPage, sortColumn, sortOrder, debounceSearch, refresh, filteredBrand, filteredCategory, filteredSubCategory]);

    useEffect(() => {
    }, [refresh])

    const handlePageChange = (page) => setPage(page);
    const handleRowsPerPageChange = (newPerPage, page) => {
        setRowsPerPage(newPerPage);
        setPage(page);
    };

    const handleSort = (column, sortDirection) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortOrder(sortDirection);
        }
    };

    const columns = [
        {
            name: "Product Name",
            selector: (row) => row.name,
            sortField: "name",
            sortable: true,
        },
        visibleColumns.description && {
            name: "Description",
            sortField: "description",
            sortable: true,
            cell: (row) => (
                <>
                    <span
                        id={`desc-tooltip-${row._id}`}
                        style={{
                            maxWidth: "150px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                        }}
                    >
                        {row.description}
                    </span>
                    <UncontrolledTooltip target={`desc-tooltip-${row._id}`} placement="top">
                        {row.description}
                    </UncontrolledTooltip>
                </>
            ),
        },
        visibleColumns.descriptionAr && {
            name: "Description (AR)",
            sortField: "descriptionAr",
            sortable: true,
            cell: (row) => (
                <>
                    <span
                        id={`descAr-tooltip-${row._id}`}
                        style={{
                            maxWidth: "150px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                        }}
                    >
                        {row.descriptionAr}
                    </span>
                    <UncontrolledTooltip target={`descAr-tooltip-${row._id}`} placement="top">
                        {row.descriptionAr}
                    </UncontrolledTooltip>
                </>
            ),
        },
        visibleColumns.category && {
            name: "Category",
            selector: (row) => row.category?.name,
            sortField: "category.name",
            sortable: true,
        },
        {
            name: "Price",
            selector: (row) => `KWD ${row.finalPrice?.toFixed(2)}`,
            sortField: "finalPrice",
            sortable: true,
        },
        visibleColumns.stock && {
            name: "Stock",
            selector: (row) => row.stock,
            sortField: "stock",
            sortable: true,
            cell: (row) => (
                <Badge color={row.stock < 10 ? "danger" : "success"}>
                    {row.stock} {row.stock < 10 ? "(Low)" : ""}
                </Badge>
            ),
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortField: "status",
            sortable: true,
            cell: (row) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ position: "relative" }}>
                        <input
                            type="checkbox"
                            checked={row.status === "active"}
                            onChange={() => onDeactivate(row)}
                            style={{
                                width: "40px",
                                height: "20px",
                                appearance: "none",
                                backgroundColor: row.status === "active" ? "#28c76f" : "#ea5455",
                                borderRadius: "10px",
                                position: "relative",
                                cursor: "pointer",
                                outline: "none",
                                transition: "background-color 0.3s ease"
                            }}
                        />
                        <div
                            style={{
                                position: "absolute",
                                top: "2px",
                                left: row.status === "active" ? "22px" : "2px",
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#fff",
                                borderRadius: "50%",
                                transition: "left 0.3s ease",
                                pointerEvents: "none"
                            }}
                        />
                    </div>
                </div>
            )
        },
        {
            name: "Actions",
            cell: (row) => (
                <Dropdown isOpen={dropdownOpen === row._id} toggle={() => toggleDropdown(row._id)}>
                    <DropdownToggle color="dark" size="sm" className="border-0 shadow-none">
                        <MoreHorizontal size={18} />
                    </DropdownToggle>
                    <DropdownMenu end>
                        <DropdownItem onClick={() => onEdit(row)} className="d-flex align-items-center">
                            <Edit size={14} className="me-2" /> Edit
                        </DropdownItem>
                        <DropdownItem onClick={() => onDelete(row)} className="d-flex align-items-center text-danger">
                            <Trash2 size={14} className="me-2" /> Delete
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ].filter(Boolean); // Filter out hidden columns

    const customStyles = {
        headCells: {
            style: { backgroundColor: "black", color: "white", fontWeight: "bold", textAlign: "center" },
        },
        cells: { style: { textAlign: "center" } },
    };

    return (
        <>

            {loading ? (
                <Loader />
            ) : showTable ? (
                <DataTable
                    columns={columns}
                    data={data}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    highlightOnHover
                    striped
                    customStyles={customStyles}
                    progressPending={loading}
                />
            ) : (
                <ProductCard
                    data={data}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onPageChange={handlePageChange}
                    totalItems={totalRows}
                    currentPage={page}
                    pageSize={rowsPerPage}
                    onUpdateStatus={onDeactivate}
                />
            )}
        </>
    );
};

export default ProductTable;
